import { StrictMode, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { createBackCoachClient } from "@backcoach/api-client";
import { ApiProvider, BackCoachTile, OrgDashboard, unwrap, useAdminSession, useApi, useResource } from "@backcoach/ui";
import type { AdminSession } from "@backcoach/ui";
import "@backcoach/ui/styles.css";
import "./console.css";
import { AdminsScreen } from "./Admins.js";
import { OrgSettingsScreen } from "./OrgSettings.js";
import { OrganisationsScreen, PoliciesScreen, ReviewsScreen } from "./Platform.js";
import { SignInScreen } from "./SignIn.js";

/**
 * The admin console. For an org admin: the dashboard, org defaults with
 * leavers and the audit log, and admin colleagues. For a platform admin:
 * organisations, workstation reviews and policies, plus any org's own
 * screens. Sign-in by magic link, a first-party session cookie, and no
 * participant screen anywhere here.
 */
const API_URL = (import.meta as unknown as { env: Record<string, string | undefined> }).env["VITE_API_URL"] ?? "http://localhost:8787";

type View = "dashboard" | "settings" | "admins" | "orgs" | "reviews" | "policies";

function Console() {
  const client = useMemo(() => createBackCoachClient({ baseUrl: API_URL, credentials: "include" }), []);
  return (
    <ApiProvider client={client}>
      <Gate />
    </ApiProvider>
  );
}

function Gate() {
  const session = useAdminSession();
  if (session.loading && session.data === null) return <div className="bc-app" aria-busy />;
  if (!session.data) return <SignInScreen onSignedIn={session.reload} />;
  return <Shell admin={session.data} onSignedOut={session.reload} />;
}

/**
 * The bar has two scopes and says so: Platform (every organisation, the
 * review queue, the policies) for platform admins, and one Organisation,
 * named in the switcher, for its dashboard, settings and admins. A platform
 * admin can switch to any organisation; an org admin sees only their own.
 */
function Shell({ admin, onSignedOut }: { admin: AdminSession; onSignedOut: () => void }) {
  const api = useApi();
  const platform = admin.role === "platform_admin";
  const [view, setView] = useState<View>(platform ? "orgs" : "dashboard");
  const [org, setOrg] = useState<{ id: string; name: string } | null>(admin.orgs[0] ?? null);
  const all = useResource(async (a) => (platform ? unwrap(await a.GET("/v1/platform/orgs")) : []));
  const choices: { id: string; name: string }[] = platform ? (all.data ?? []).map((o) => ({ id: o.id, name: o.name })) : admin.orgs;
  const orgKnown = org && choices.some((o) => o.id === org.id) ? org : null;
  const options = orgKnown || !org ? choices : [org, ...choices];

  const Nav = ({ id, children }: { id: View; children: string }) => (
    <button type="button" className={`bc-navbtn${view === id ? " on" : ""}`} onClick={() => setView(id)}>
      {children}
    </button>
  );

  return (
    <div className="bc-app bc-app-admin">
      <header className="bc-topbar bc-topbar-admin">
        <div className="bc-brand">
          <span className="bc-brand-icon">
            <BackCoachTile size={26} />
          </span>
          <span className="bc-brand-word">Back Coach</span>
          <span className="bc-brand-role">Console</span>
        </div>
        <nav className="bc-nav" aria-label="Console">
          {platform && (
            <div className="bc-navgroup">
              <span className="bc-navlabel">Platform</span>
              <Nav id="orgs">Organisations</Nav>
              <Nav id="reviews">Reviews</Nav>
              <Nav id="policies">Policies</Nav>
            </div>
          )}
          {org && (
            <div className="bc-navgroup">
              <span className="bc-navlabel">Organisation</span>
              {options.length > 1 ? (
                <select
                  className="bc-navselect"
                  aria-label="Organisation"
                  value={org.id}
                  onChange={(e) => setOrg(options.find((o) => o.id === e.target.value) ?? org)}
                >
                  {options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="bc-navorg">{org.name}</span>
              )}
              <Nav id="dashboard">Dashboard</Nav>
              <Nav id="settings">Settings</Nav>
              <Nav id="admins">Admins</Nav>
            </div>
          )}
        </nav>
        <p className="bc-admin-who">{admin.email}</p>
        <button type="button" className="bc-btn bc-btn-quiet" onClick={() => void api.POST("/v1/auth/sign-out").finally(onSignedOut)}>
          Sign out
        </button>
      </header>
      <main className="bc-main">
        {!platform && !org && <p className="bc-note">No organisation is assigned to this account.</p>}
        {view === "orgs" && (
          <OrganisationsScreen
            onOpen={(o) => {
              setOrg(o);
              setView("dashboard");
            }}
          />
        )}
        {view === "reviews" && <ReviewsScreen />}
        {view === "policies" && <PoliciesScreen />}
        {org && view === "dashboard" && <OrgDashboard orgId={org.id} />}
        {org && view === "settings" && <OrgSettingsScreen orgId={org.id} />}
        {org && view === "admins" && <AdminsScreen orgId={org.id} />}
      </main>
    </div>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("Root element #root not found");
createRoot(root).render(
  <StrictMode>
    <Console />
  </StrictMode>,
);
